import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './SimpleDropdown.css';

interface DropdownOption {
  value: string;
  label: string;
}

interface SimpleDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const SimpleDropdown: React.FC<SimpleDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Click to assign',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is outside both the dropdown container and the portal menu
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);
      const isOutsideMenu = !document.querySelector('.dropdown-menu')?.contains(target);
      
      if (isOutsideDropdown && isOutsideMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const calculateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      // Calculate space below for positioning (not used in current implementation but kept for reference)
      
      // Always position below for simplicity, but ensure it's visible
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 150)
      });
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('SimpleDropdown: Toggle clicked, current isOpen:', isOpen);
    console.log('SimpleDropdown: Options available:', options.length);
    
    if (!isOpen) {
      calculateDropdownPosition();
      console.log('SimpleDropdown: Position calculated, opening dropdown');
    }
    
    setIsOpen(!isOpen);
  };

  const handleOptionSelect = (option: DropdownOption, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('SimpleDropdown: Option selected:', option);
    onChange(option.value);
    setIsOpen(false);
  };

  const selectedOption = options.find(option => option.value === value);

  return (
    <div className={`simple-dropdown ${className}`} ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        className="dropdown-trigger"
        onClick={handleToggle}
      >
        {selectedOption ? selectedOption.label : placeholder}
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && createPortal(
        <div 
          className="dropdown-menu"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 99999
          }}
        >
          <ul className="dropdown-options">
            {options.map((option) => (
              <li
                key={option.value}
                className={`dropdown-option ${option.value === value ? 'selected' : ''}`}
                onClick={(e) => handleOptionSelect(option, e)}
              >
                {option.label}
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SimpleDropdown;